import { duplicateLayerInSession } from '../public-format/layer-clipboard';
import { throwIfAborted } from '../codecs/types';
import type { ActionStep, RecordedAction } from './action-types';
import { AddLayerCommand, changed, ClearRasterSelectionCommand, ContentAwareFillCommand, FillRasterCommand, noOp, rejected, TransformLayerCommand, UpdateAdjustmentCommand, type Command, type CommandExecution, type HistoryManager } from './commands';
import { damage } from './damage';
import { createAdjustmentLayer, createId, findLayer, type EditorDocument } from './model';
import type { DocumentSession } from './session';

export const ACTIONS_EXTENSION = 'maipo:actions';

export interface ReplayResult {
  completed: boolean;
  failedStepIndex?: number;
  reason?: string;
}

function validStep(value: unknown): value is ActionStep {
  if (!value || typeof value !== 'object' || typeof (value as { type?: unknown }).type !== 'string') return false;
  return ['fill-active-raster', 'content-aware-fill', 'clear-selected-pixels', 'transform-active-layer', 'duplicate-active-layer', 'add-adjustment', 'update-active-adjustment', 'marker'].includes((value as { type: string }).type);
}

export function documentActions(document: EditorDocument): RecordedAction[] {
  const source = document.extensions[ACTIONS_EXTENSION];
  if (!Array.isArray(source)) return [];
  return source.flatMap((candidate): RecordedAction[] => {
    if (!candidate || typeof candidate !== 'object') return [];
    const action = candidate as Record<string, unknown>;
    if (typeof action.id !== 'string' || typeof action.name !== 'string' || !Array.isArray(action.steps)) return [];
    return [{ id: action.id, name: action.name.slice(0, 200), steps: action.steps.map((step, index) => validStep(step)
      ? structuredClone(step)
      : { type: 'marker', label: `Unsupported action step ${index + 1}`, replayable: false }) }];
  });
}

class UpdateActionsCommand implements Command {
  readonly label: string;
  private before?: RecordedAction[];

  constructor(label: string, private readonly update: (actions: readonly RecordedAction[]) => RecordedAction[]) {
    this.label = label;
  }

  retainedBytes(): number { return 256 + JSON.stringify(this.before ?? []).length * 2; }
  toActionStep(): ActionStep { return { type: 'marker', label: this.label, replayable: false }; }

  execute(document: EditorDocument): CommandExecution {
    this.before ??= documentActions(document);
    const after = this.update(documentActions(document));
    if (JSON.stringify(after) === JSON.stringify(documentActions(document))) return noOp();
    document.extensions[ACTIONS_EXTENSION] = structuredClone(after);
    return changed(damage({ structural: true }));
  }

  undo(document: EditorDocument): void {
    if (this.before?.length) document.extensions[ACTIONS_EXTENSION] = structuredClone(this.before);
    else delete document.extensions[ACTIONS_EXTENSION];
  }
}

export class AddActionCommand extends UpdateActionsCommand {
  constructor(action: RecordedAction) {
    super(`Add action ${action.name}`, (actions) => [...actions, structuredClone(action)]);
  }
}

export class RenameActionCommand extends UpdateActionsCommand {
  constructor(actionId: string, name: string) {
    super('Rename action', (actions) => actions.map((action) => action.id === actionId ? { ...action, name: name.slice(0, 200) } : action));
  }
}

export class DeleteActionCommand extends UpdateActionsCommand {
  constructor(actionId: string) {
    super('Delete action', (actions) => actions.filter((action) => action.id !== actionId));
  }
}

export function newRecordedAction(name: string, steps: readonly ActionStep[]): RecordedAction {
  return { id: createId('action'), name: name.trim().slice(0, 200) || 'Untitled action', steps: steps.map((step) => structuredClone(step)) };
}

function currentLayer(document: EditorDocument) {
  const id = document.activeLayerIds[0];
  return id ? findLayer(document.root, id) : undefined;
}

function executeStep(document: EditorDocument, history: HistoryManager, step: ActionStep): CommandExecution {
  if (step.type === 'marker') return rejected(`“${step.label}” was recorded as a non-replayable marker.`);
  const active = currentLayer(document);
  if (!active) return rejected('No active layer is available.');
  if (step.type === 'fill-active-raster') {
    if (active.kind !== 'raster') return rejected('The active layer is not raster.');
    return history.execute(document, new FillRasterCommand(active.id, structuredClone(step.definition)));
  }
  if (step.type === 'content-aware-fill') {
    if (active.kind !== 'raster') return rejected('The active layer is not raster.');
    return history.execute(document, new ContentAwareFillCommand(active.id));
  }
  if (step.type === 'clear-selected-pixels') {
    if (active.kind !== 'raster') return rejected('The active layer is not raster.');
    return history.execute(document, new ClearRasterSelectionCommand(active.id));
  }
  if (step.type === 'transform-active-layer') {
    if (!Array.isArray(step.transform) || step.transform.length !== 6 || !step.transform.every(Number.isFinite)) return rejected('The recorded transform is invalid.');
    const command = new TransformLayerCommand(active.id);
    const begun = history.begin(document, command);
    if (begun.outcome !== 'changed') return begun;
    history.updateLive(document, (live) => { if (live === command) command.update(document, step.transform); });
    return history.commitLive(document);
  }
  if (step.type === 'duplicate-active-layer') return rejected('Duplication requires asynchronous session replay.');
  if (step.type === 'add-adjustment') {
    const layer = createAdjustmentLayer();
    layer.adjustment = structuredClone(step.adjustment);
    return history.execute(document, new AddLayerCommand(layer));
  }
  if (active.kind !== 'adjustment') return rejected('The active layer is not an adjustment layer.');
  return history.execute(document, new UpdateAdjustmentCommand(active.id, structuredClone(step.patch)));
}

const replaying = new WeakSet<DocumentSession>();

/** Each completed step remains an ordinary undoable edit. Never roll back unrelated
 * edits when preparation fails or a run is cancelled. */
export async function replayAction(session: DocumentSession, action: RecordedAction, options: { signal?: AbortSignal } = {}): Promise<ReplayResult> {
  if (replaying.has(session)) return { completed: false, failedStepIndex: 0, reason: 'An action is already playing in this document.' };
  replaying.add(session);
  let index = 0;
  try {
    throwIfAborted(options.signal);
    const steps = structuredClone(action.steps);
    let binding = session.captureRevision();
    let selection = JSON.stringify(session.document.activeLayerIds);
    for (const step of steps) {
      throwIfAborted(options.signal);
      if (!validStep(step)) return { completed: false, failedStepIndex: index, reason: 'The recorded action step is unsupported.' };
      if (!session.isCurrent(binding) || session.history.hasLiveCommand || JSON.stringify(session.document.activeLayerIds) !== selection) {
        return { completed: false, failedStepIndex: index, reason: 'The document or selection changed during action replay.' };
      }
      const active = currentLayer(session.document);
      // Replayed commits are attributed to the action (History shows "Action · <name>").
      const origin = { kind: 'replay', action: action.name } as const;
      const result = step.type === 'duplicate-active-layer'
        ? active ? await duplicateLayerInSession(session, active.id, options) : rejected('No active layer is available.')
        : session.history.withOrigin(origin, () => executeStep(session.document, session.history, step));
      // The duplicate commits after an await, where an ambient origin could capture a user edit; attribute it afterwards.
      const top = session.history.top();
      if (step.type === 'duplicate-active-layer' && result.outcome === 'changed' && top) session.ledger.reattributeLatest(top, origin);
      if (result.outcome === 'rejected') return { completed: false, failedStepIndex: index, reason: result.reason };
      // Preparation may yield. Only this step's own commit may advance history.
      if (!session.isCurrent({ ...binding, revision: binding.revision + (result.outcome === 'changed' ? 1 : 0) }) || session.history.hasLiveCommand) {
        return { completed: false, failedStepIndex: index, reason: 'The document changed during action replay.' };
      }
      binding = session.captureRevision();
      selection = JSON.stringify(session.document.activeLayerIds);
      index += 1;
      if (index < steps.length) await new Promise<void>(resolve => setTimeout(resolve, 0));
    }
    return { completed: true };
  } catch (reason) {
    return { completed: false, failedStepIndex: index, reason: reason instanceof Error ? reason.message : 'Action replay failed.' };
  } finally { replaying.delete(session); }
}

/** Key by tab identity: two open copies of a saved document share a document ID. */
export async function replayActionBatch(sessions: readonly DocumentSession[], action: RecordedAction, options: { signal?: AbortSignal } = {}): Promise<Map<string, ReplayResult>> {
  const captured = sessions.map(session => ({ session, revision: session.currentRevision, selection: JSON.stringify(session.document.activeLayerIds) }));
  const snapshot = structuredClone(action);
  const results = new Map<string, ReplayResult>();
  for (const { session, revision, selection } of captured) {
    const result = !session.isCurrent({ sessionId: session.sessionId, revision }) || JSON.stringify(session.document.activeLayerIds) !== selection
      ? { completed: false, failedStepIndex: 0, reason: 'The document changed before its action could start.' }
      : await replayAction(session, snapshot, options);
    results.set(session.sessionId, result);
  }
  return results;
}
